import React, {useMemo} from 'react';
import Preloader from "../../components/Preloader";
import Page from "../../components/Page";
import Error from "../404/Error";
import Separator from "../../components/Separator";
import InnerPage from "../../components/InnerPage";
import Container from "../../components/Container";
import Card from "../../components/Card";
import missionBG from "./assets/bg_mission.webp";
import valuesBG from "./assets/bg_values.webp";
import profBG from "./assets/bg_professionalism.webp";
import openBG from "./assets/bg_openDoor.webp";
import fireBG from "./assets/bg_fire.webp";
import box from "./assets/box.png";
import bark from "./assets/bark.png";
import pirate from "./assets/pirate.png";
import openDoor from "./assets/openDoor.png";
import octopus from "./assets/octopus.png";
import { useMission } from "../../api/api";
import { BlocksRenderer } from "@strapi/blocks-react-renderer";
import { useTranslation } from "react-i18next";
import useImagePreloader from "../../utils/useImagePreloader";
import { Helmet } from "react-helmet";
import styles from "./mission.module.scss";

const preloadSrcList = [
  missionBG,
  valuesBG,
  profBG,
  openBG,
  fireBG,
  box,
  bark,
  pirate,
  openDoor,
  octopus
];

const Mission = () => {
  const { imagesPreloaded } = useImagePreloader(preloadSrcList);
  const [t, i18n] = useTranslation("global");
  const lang = useMemo(() => i18n.language.slice(0, 2), [i18n.language]);

  const { missionData, isMissionLoading, missionError } = useMission(lang);

  if (isMissionLoading || !imagesPreloaded) {
    return <Preloader />
  }

  if (missionError || missionData.error) {
    return <Error />
  }

  const data = missionData.data;

  return (
    <Page>
      <Helmet>
        <title>RedBark Studio | {t("pages.mission")}</title>
      </Helmet>
      <InnerPage className={styles.wrapper}>
        <Separator/>
        { data &&
          <>
            <div className={styles.top}>
              <div className={styles.backImg}>
                <img src={missionBG} alt='missionBG' />
              </div>
              <Container className={styles.page}>
                <h2 className={`text-pink text-center ${styles.title}`}>{data.title}</h2>
                <div className={styles.topRow}>
                  <h3 className={styles.topDescTitle}>{data.left_text}</h3>
                  <div className={styles.topBox}>
                    <img src={box} alt='box' />
                  </div>
                  <div className={styles.topDesc}>
                    <div className="text-large">
                      <p>{data.right_text}</p>
                    </div>
                  </div>

                  <div className={`${styles.topUnderTitle} text-center `}>
                    <h3 className="text-pink">{data.slogan}</h3>
                    <p className="text-large">{data.our_purpose}</p>
                  </div>
                </div>
              </Container>
            </div>
            <div className={styles.values}>
              <div className={`${styles.backImg} ${styles.valuesBG}`}>
                <img src={valuesBG} alt='valuesBG' />
              </div>
              <Container className={styles.team}>
                <h3 className={`text-pink text-center ${styles.title} ${styles.valuesTitle}`}>{data.our_values}</h3>
                <div className={`row flex-col-center-h ${styles.valuesContent}`}>
                  <div className="col-xl-6 col-lg-6 col-md-6 col-sm-12">
                    <Card text={data.team.title} img={bark} classNameImg={styles.cardImg} className={styles.card}/>
                  </div>
                  <div className={`col-xl-6 col-lg-6 col-md-6 col-sm-12 text-large ${styles.rightDesc}`}>
                    <BlocksRenderer content={data.team.text} />
                  </div>
                </div>
              </Container>
            </div>
            <div className={styles.values}>
              <div className={`${styles.backImg} ${styles.valuesBG}`}>
                <img src={profBG} alt='profBG' />
                <img className={styles.fireBG} src={fireBG} alt='fireBG' />
              </div>
              <Container className={styles.professionalism}>
                <div className={`row flex-col-center-h ${styles.professionalismContent}`}>
                  <div className={`col-xl-6 col-lg-6 col-md-6 col-sm-12 text-large`}>
                    <BlocksRenderer content={data.professionalism.text} />
                  </div>
                  <div className="col-xl-6 col-lg-6 col-md-6 col-sm-12">
                    <Card
                      text={data.professionalism.title}
                      img={pirate}
                      classNameImg={styles.pirateImg}
                      className={`${styles.pirateCard} ${styles.card}`}
                    />
                  </div>
                </div>
              </Container>
            </div>
            <div className={`${styles.values} ${styles.openDoorWrap}`}>
              <div className={`${styles.backImg} ${styles.openDoorBG}`}>
                <img src={openBG} alt='openDoorBG' />
              </div>
              <Container className={styles.openDoor}>
                <div className={`row flex-col-center-h`}>
                  <div className="col-xl-6 col-lg-6 col-md-6 col-sm-12">
                    <Card text={data.openness.title} img={openDoor} classNameImg={styles.openDoorImg} className={styles.card}/>
                  </div>
                  <div className={`col-xl-6 col-lg-6 col-md-6 col-sm-12 text-large ${styles.rightDesc}`}>
                    <BlocksRenderer content={data.openness.text} />
                  </div>
                </div>
              </Container>
            </div>
            <div className={`${styles.values} ${styles.flexibilityWrap}`}>
              <div className={`${styles.backImg} ${styles.valuesBG}`}></div>
              <Container className={styles.flexibility}>
                <div className={`row flex-col-center-h ${styles.flexibilityContent}`}>
                  <div className={`col-xl-6 col-lg-6 col-md-6 col-sm-12 text-large`}>
                    <BlocksRenderer content={data.flexibility.text} />
                  </div>
                  <div className="col-xl-6 col-lg-6 col-md-6 col-sm-12">
                    <Card
                      text={data.flexibility.title}
                      img={octopus}
                      classNameImg={styles.flexibilityImg}
                      className={`${styles.card} ${styles.flexibilityCard}`}
                    />
                  </div>
                </div>
              </Container>
            </div>
          </>
        }
      </InnerPage>
    </Page>
  );
};

export default Mission;